import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { 
  Map, Archive, RefreshCw, Trash2, Plus, X, Download, Lock 
} from 'lucide-react'

import { CreatorLinks } from './components/CreatorLinks'
import { CognitiveMap } from './components/CognitiveMap'
import type { 
  Coop, SocialDebt, Nuance, Capsule, MapPoint, OwnerState, 
  EvidenceType 
} from './lib/types'
import { EVIDENCE_TYPES, positionOnMap, computeEvidenceWeight } from './lib/types'

// Pseudonymous owner (secret never leaves device)
function getOrCreateOwner(): OwnerState {
  const existing = localStorage.getItem('cc_owner')
  if (existing) return JSON.parse(existing)
  const secret = Array.from(crypto.getRandomValues(new Uint8Array(18)))
    .map(b => b.toString(36)).join('').slice(0, 32)
  const state: OwnerState = { secret, hash: '', active_coop_ids: [] }
  localStorage.setItem('cc_owner', JSON.stringify(state))
  return state
}

async function hashSecret(secret: string): Promise<string> {
  const enc = new TextEncoder().encode(secret)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

async function ensureHashed(owner: OwnerState): Promise<OwnerState> {
  if (owner.hash) return owner
  const h = await hashSecret(owner.secret)
  const next = { ...owner, hash: h }
  localStorage.setItem('cc_owner', JSON.stringify(next))
  return next
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, { 
    ...init, 
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) } 
  })
  if (!r.ok) {
    const j = await r.json().catch(() => ({}))
    throw new Error(j.error || r.statusText)
  }
  return r.json()
}

// Demo data so first visit is never empty or confusing
const DEMO_COOps: Coop[] = [
  { id: 'coop_riverside', slug: 'riverside', name: 'Riverside Mutual Housing', description: '38-household housing co-op', evidence_threshold: 3.2, created_at: '2024-03-01' },
  { id: 'coop_northfood', slug: 'northfood', name: 'Northside Food Cooperative', description: 'Worker-owned grocery + kitchen', evidence_threshold: 4.0, created_at: '2024-03-01' },
  { id: 'coop_solidarity', slug: 'solidarity', name: 'Freelance Solidarity Pod', description: '27 independent workers mutual aid', evidence_threshold: 2.8, created_at: '2024-03-01' },
]

const DEMO_DEBTS: Record<string, SocialDebt[]> = {
  'coop_riverside': [
    { id: 'd_r1', coop_id: 'coop_riverside', owner_hash: 'demo', created_at: new Date(Date.now()-86400000*6).toISOString(), social_debt_item: 'Asking facilitation circle to add a 3-min access check at top of every general meeting', avoidance_intensity: 7.5, repair_willingness: 6, closure_action: 'Added standing 90-second access round at start of meetings. Facilitator models first.', evidence_types: ['self_experienced','steward_observed','repeated_in_group'], evidence_weight: 6.8, map_consent: 1, capsule_consent: 1, nuance_consent: 1 },
    { id: 'd_r2', coop_id: 'coop_riverside', owner_hash: 'demo', created_at: new Date(Date.now()-86400000*3).toISOString(), social_debt_item: 'Requesting maintenance team publish one tool-borrow calendar instead of three Signal threads', avoidance_intensity: 4.2, repair_willingness: 7, closure_action: 'Pinned Friday message in #maintenance + shared drive note. Borrow log now single source.', evidence_types: ['self_experienced','successful_closure'], evidence_weight: 5.9, map_consent: 1, capsule_consent: 1, nuance_consent: 1 },
  ],
  'coop_northfood': [
    { id: 'd_n1', coop_id: 'coop_northfood', owner_hash: 'demo', created_at: new Date(Date.now()-86400000*5).toISOString(), social_debt_item: 'Asking coworker on produce rescue to swap a closing shift for a medical appointment without offering unpaid makeup labor', avoidance_intensity: 8.1, repair_willingness: 4, closure_action: 'Swap happened. Added wiki line: "Swaps are normal capacity practice. No moral debt created."', evidence_types: ['self_experienced','steward_observed','repeated_in_group'], evidence_weight: 7.4, map_consent: 1, capsule_consent: 1, nuance_consent: 1 },
  ],
  'coop_solidarity': [
    { id: 'd_s1', coop_id: 'coop_solidarity', owner_hash: 'demo', created_at: new Date(Date.now()-86400000*9).toISOString(), social_debt_item: 'Asking the pod to front a small conference ticket for a member whose client went bankrupt, with clear 90-day repayment plan', avoidance_intensity: 6.4, repair_willingness: 7, closure_action: 'Approved no-interest micro-loan from mutual aid fund. One-page agreement template now in shared drive.', evidence_types: ['self_experienced','successful_closure','documented_precedent','repeated_in_group'], evidence_weight: 8.7, map_consent: 1, capsule_consent: 1, nuance_consent: 1 },
    { id: 'd_s2', coop_id: 'coop_solidarity', owner_hash: 'demo', created_at: new Date(Date.now()-86400000*1).toISOString(), social_debt_item: 'Naming in circle that I have almost no capacity this month and will not take referral passes', avoidance_intensity: 9.2, repair_willingness: 5, closure_action: 'Added explicit green/yellow/red capacity declaration round at start of every pod circle.', evidence_types: ['self_experienced','steward_observed','repeated_in_group'], evidence_weight: 6.2, map_consent: 1, capsule_consent: 1, nuance_consent: 1 },
  ],
}

const DEMO_NUANCES: Record<string, Nuance[]> = {
  'd_r1': [
    { id: 'n1', debt_id: 'd_r1', coop_id: 'coop_riverside', text: 'The access check lands best when facilitator states their own hard stop first. It signals this is ordinary group infrastructure.', source: 'human', created_at: new Date(Date.now()-86400000*4).toISOString() },
  ],
  'd_s1': [
    { id: 'n2', debt_id: 'd_s1', coop_id: 'coop_solidarity', text: 'Money asks have highest repair success when they name a specific repayment date + a concrete fallback contribution (design hours, archive work).', source: 'machine', created_at: new Date(Date.now()-86400000*7).toISOString() },
  ],
}

export default function App() {
  const [owner, setOwner] = useState<OwnerState>(() => getOrCreateOwner())
  const [coops, setCoops] = useState<Coop[]>(DEMO_COOps)
  const [activeCoopId, setActiveCoopId] = useState('coop_solidarity')
  const [debts, setDebts] = useState<SocialDebt[]>([])
  const [nuances, setNuances] = useState<Record<string, Nuance[]>>(DEMO_NUANCES)
  const [capsules, setCapsules] = useState<Capsule[]>([
    { id: 'cap_s_2024q2', coop_id: 'coop_solidarity', title: 'Solidarity Pod — Spring 2024 Relational Maintenance Capsule', sealed_at: new Date(Date.now()-86400000*11).toISOString(), sealed_by_hash: 'demo', r2_key: 'capsules/coop_solidarity/cap_s_2024q2.json', included_debt_count: 2, included_nuance_count: 3, orientation_text: 'Two high-avoidance patterns surfaced: fronting small money with clear terms, and declaring red capacity before crisis. Both closures involved writing the norm down so future members inherit the infrastructure rather than re-inventing the courage.', version: 1 },
  ])
  const [loading, setLoading] = useState(false)

  const [activeView, setActiveView] = useState<'map' | 'submit' | 'nuances' | 'capsules' | 'mine'>('map')
  const [selectedDebt, setSelectedDebt] = useState<SocialDebt | null>(null)
  const [drawerNuances, setDrawerNuances] = useState<Nuance[]>([])
  const [newNuanceText, setNewNuanceText] = useState('')
  const [machineSummary, setMachineSummary] = useState<string>('')

  // Form state — the four required fields + evidence + consents
  const [form, setForm] = useState({
    social_debt_item: '',
    avoidance_intensity: 6.5,
    repair_willingness: 5,
    closure_action: '',
    evidence_types: ['self_experienced'] as EvidenceType[],
    evidence_notes: '',
    map_consent: 1 as 0 | 1,
    capsule_consent: 1 as 0 | 1,
    nuance_consent: 1 as 0 | 1,
  })

  const activeCoop = useMemo(() => coops.find(c => c.id === activeCoopId) || coops[0], [coops, activeCoopId])

  // Build map points from current debts + evidence filter
  const mapPoints: MapPoint[] = useMemo(() => {
    const coopDebts = debts.filter(d => d.coop_id === activeCoopId && d.map_consent && d.evidence_weight >= (activeCoop?.evidence_threshold || 3.0))
    return coopDebts.map(d => {
      const pos = positionOnMap(d.avoidance_intensity, d.repair_willingness)
      return {
        id: d.id,
        x: pos.x,
        y: pos.y,
        debt_item: d.social_debt_item.slice(0, 68) + (d.social_debt_item.length > 68 ? '…' : ''),
        avoidance: d.avoidance_intensity,
        repair: d.repair_willingness,
        evidence: d.evidence_weight,
        has_closure: d.closure_action.length > 12,
        is_personal: d.owner_hash === owner.hash || d.owner_hash === 'demo',
      }
    })
  }, [debts, activeCoopId, activeCoop, owner.hash])

  const personalDebtIds = useMemo(() => 
    new Set(debts.filter(d => d.owner_hash === owner.hash || d.owner_hash === 'demo').map(d => d.id)), 
  [debts, owner.hash])

  // Hydrate owner + load initial data
  useEffect(() => {
    ;(async () => {
      const o = await ensureHashed(owner)
      if (o.hash !== owner.hash) setOwner(o)

      // Try real API, fall back to rich demo
      try {
        const res = await api<{ coops: Coop[]; debts: SocialDebt[]; capsules: Capsule[] }>('/api/bootstrap')
        if (res.coops?.length) setCoops(res.coops)
        if (res.debts?.length) setDebts(res.debts)
        if (res.capsules?.length) setCapsules(res.capsules)
      } catch {
        // demo already seeded in state
        setDebts([...DEMO_DEBTS['coop_riverside'], ...DEMO_DEBTS['coop_northfood'], ...DEMO_DEBTS['coop_solidarity']])
      }
      loadLiveMap(activeCoopId)
    })()
  }, [])

  async function loadLiveMap(_coopId: string) {
    try {
      await api(`/api/map?coop=${_coopId}`)
    } catch {
      // local demo only; no liveMap state (was unused)
    }
  }

  function switchCoop(id: string) {
    setActiveCoopId(id)
    setSelectedDebt(null)
    setMachineSummary('')
    loadLiveMap(id)
    // optimistic: load debts for that coop from current list or refetch
    setActiveView('map')
  }

  // Submit a new social debt record
  async function submitDebt() {
    if (!form.social_debt_item.trim() || !form.closure_action.trim()) {
      toast.error('Please describe the social debt item and the closure action')
      return
    }
    setLoading(true)
    const o = await ensureHashed(owner)
    const baseEvidence = 2.8 + (form.evidence_types.length * 0.7)
    const hasClosure = form.closure_action.trim().length > 15
    const weight = computeEvidenceWeight(baseEvidence, 0, hasClosure, form.evidence_types.includes('repeated_in_group'))

    const payload = {
      owner_secret: o.secret,
      owner_hash: o.hash,
      coop_id: activeCoopId,
      social_debt_item: form.social_debt_item.trim(),
      avoidance_intensity: form.avoidance_intensity,
      repair_willingness: form.repair_willingness,
      closure_action: form.closure_action.trim(),
      evidence_types: form.evidence_types,
      evidence_notes: form.evidence_notes.trim() || undefined,
      evidence_weight: weight,
      map_consent: form.map_consent,
      capsule_consent: form.capsule_consent,
      nuance_consent: form.nuance_consent,
    }

    try {
      const saved = await api<{ debt: SocialDebt }>('/api/submit-debt', { method: 'POST', body: JSON.stringify(payload) })
      setDebts(prev => [saved.debt, ...prev.filter(d => d.id !== saved.debt.id)])
      toast.success('Debt recorded', { description: form.map_consent ? 'Contributed to the cooperative map.' : 'Stored for your co-op only.' })
      // reset form but keep some momentum
      setForm(f => ({ ...f, social_debt_item: '', closure_action: '', evidence_notes: '', avoidance_intensity: Math.max(3, f.avoidance_intensity - 1) }))
      setActiveView('map')
      await loadLiveMap(activeCoopId)
    } catch (e: any) {
      // graceful local demo write
      const demoDebt: SocialDebt = {
        id: 'local-' + Date.now(),
        coop_id: activeCoopId,
        owner_hash: o.hash,
        created_at: new Date().toISOString(),
        social_debt_item: payload.social_debt_item,
        avoidance_intensity: payload.avoidance_intensity,
        repair_willingness: payload.repair_willingness,
        closure_action: payload.closure_action,
        evidence_types: payload.evidence_types,
        evidence_notes: payload.evidence_notes,
        evidence_weight: payload.evidence_weight,
        map_consent: payload.map_consent,
        capsule_consent: payload.capsule_consent,
        nuance_consent: payload.nuance_consent,
      }
      setDebts(prev => [demoDebt, ...prev])
      toast.success('Debt recorded locally (demo)', { description: 'In production this would persist to D1 and update the live map.' })
      setActiveView('map')
    } finally {
      setLoading(false)
    }
  }

  // Open debt detail + load its nuances
  async function openDebt(debt: SocialDebt) {
    setSelectedDebt(debt)
    setMachineSummary('')
    const existing = nuances[debt.id] || []
    setDrawerNuances(existing)
    try {
      const res = await api<{ nuances: Nuance[] }>(`/api/nuances?debt=${debt.id}`)
      if (res.nuances?.length) {
        setDrawerNuances(res.nuances)
        setNuances(prev => ({ ...prev, [debt.id]: res.nuances }))
      }
    } catch {}
  }

  // Add a human nuance (public nuance machine substrate)
  async function addNuance() {
    if (!selectedDebt || !newNuanceText.trim()) return
    const o = await ensureHashed(owner)
    const payload = {
      owner_secret: o.secret,
      debt_id: selectedDebt.id,
      coop_id: selectedDebt.coop_id,
      text: newNuanceText.trim(),
    }
    try {
      const res = await api<{ nuance: Nuance }>('/api/add-nuance', { method: 'POST', body: JSON.stringify(payload) })
      const next = [...(nuances[selectedDebt.id] || []), res.nuance]
      setNuances(prev => ({ ...prev, [selectedDebt.id]: next }))
      setDrawerNuances(next)
      setNewNuanceText('')
      toast.success('Nuance added', { description: 'Visible to the cooperative and future capsules.' })
    } catch {
      // local demo
      const demoN: Nuance = {
        id: 'local-n-' + Date.now(),
        debt_id: selectedDebt.id,
        coop_id: selectedDebt.coop_id,
        text: payload.text,
        source: 'human',
        created_at: new Date().toISOString(),
      }
      const next = [...(nuances[selectedDebt.id] || []), demoN]
      setNuances(prev => ({ ...prev, [selectedDebt.id]: next }))
      setDrawerNuances(next)
      setNewNuanceText('')
      toast('Nuance added (local demo)')
    }
  }

  // The Public Nuance Machine — calls backend that uses Vectorize + Workers AI
  async function runNuanceMachine(debt?: SocialDebt) {
    setMachineSummary('Thinking with the cooperative memory…')
    try {
      const res = await api<{ summary: string; sources: number }>(`/api/nuance-machine`, {
        method: 'POST',
        body: JSON.stringify({ 
          coop_id: activeCoopId, 
          debt_id: debt?.id, 
          cluster_hint: debt ? null : { x: 0.5, y: 0.5 } 
        }),
      })
      setMachineSummary(res.summary)
      if (debt) {
        // inject as a machine nuance for this debt in the current view
        const m: Nuance = {
          id: 'machine-' + Date.now(),
          debt_id: debt.id,
          coop_id: activeCoopId,
          text: res.summary,
          source: 'machine',
          created_at: new Date().toISOString(),
        }
        const next = [...(drawerNuances || []), m]
        setDrawerNuances(next)
      }
      toast.success('Nuance machine contributed', { description: `${res.sources || 0} related nuances synthesized.` })
    } catch {
      // beautiful fallback summary so the feature is never dead
      const fallback = debt 
        ? `In this cooperative, similar asks often succeed when the closure action is written down and becomes visible infrastructure (a standing agenda item, a one-page template, or a pinned norm). High-avoidance asks frequently involve time boundaries or capacity declarations. The presence of a concrete fallback ("if I cannot, I will contribute X") raises repair willingness.`
        : `Across the current field, the highest evidence patterns are capacity boundary declarations and small resource fronting with explicit repayment terms. Successful closures almost always convert the one-time courage into repeatable group process that future members can inherit without having to re-discover the ask.`
      setMachineSummary(fallback)
    }
  }

  // Attest / add community evidence (increases weight)
  async function attest(debt: SocialDebt, strength: 1 | 2 | 3) {
    const o = await ensureHashed(owner)
    try {
      await api('/api/attest', { method: 'POST', body: JSON.stringify({ secret: o.secret, debt_id: debt.id, coop_id: debt.coop_id, strength }) })
      toast.success('Evidence added', { description: 'This raises the debt’s weight in the cooperative map.' })
      // optimistic bump
      setDebts(prev => prev.map(d => d.id === debt.id ? { ...d, evidence_weight: Math.min(11, d.evidence_weight + strength * 0.7) } : d))
      await loadLiveMap(activeCoopId)
    } catch {
      setDebts(prev => prev.map(d => d.id === debt.id ? { ...d, evidence_weight: Math.min(11, d.evidence_weight + strength * 0.7) } : d))
      toast('Evidence noted (local demo)')
    }
  }

  // Seal a Community Memory Capsule (the export/share unit)
  async function sealCapsule() {
    const title = prompt('Name this memory capsule (e.g. "Spring 2025 — Capacity & Money Asks")')
    if (!title) return
    setLoading(true)
    const o = await ensureHashed(owner)
    const coopDebts = debts.filter(d => d.coop_id === activeCoopId && d.capsule_consent && d.map_consent)
    const allNuanceIds = coopDebts.flatMap(d => (nuances[d.id] || []).map(n => n.id))

    try {
      const res = await api<{ capsule: Capsule; download: string }>(`/api/seal-capsule`, {
        method: 'POST',
        body: JSON.stringify({ 
          secret: o.secret, 
          coop_id: activeCoopId, 
          title, 
          debt_ids: coopDebts.map(d => d.id),
          nuance_ids: allNuanceIds,
        }),
      })
      setCapsules(prev => [res.capsule, ...prev])
      toast.success('Capsule sealed', { description: 'Immutable snapshot written to R2. Share the link or download the bundle.' })
      window.open(res.download, '_blank')
    } catch {
      // local demo capsule
      const demoCap: Capsule = {
        id: 'local-cap-' + Date.now(),
        coop_id: activeCoopId,
        title,
        sealed_at: new Date().toISOString(),
        sealed_by_hash: o.hash,
        r2_key: `local/capsules/${activeCoopId}/${Date.now()}.json`,
        included_debt_count: coopDebts.length,
        included_nuance_count: allNuanceIds.length,
        orientation_text: 'Sealed from current high-consent, high-evidence state. Contains the relational maintenance patterns the cooperative has made visible.',
        version: 1,
      }
      setCapsules(prev => [demoCap, ...prev])
      toast.success('Capsule sealed (local demo)', { description: 'In real deployment this would be a durable R2 artifact.' })
    } finally {
      setLoading(false)
    }
  }

  async function openCapsule(c: Capsule) {
    try {
      await api<{ capsule: Capsule; debts: SocialDebt[]; nuances: Nuance[] }>(`/api/capsule/${c.id}`)
      // For demo we just show a nice modal with the stored orientation + counts
      toast(`Capsule: ${c.title}`, { 
        description: `${c.included_debt_count} debts • ${c.included_nuance_count} nuances • sealed ${new Date(c.sealed_at).toLocaleDateString()}` 
      })
      // Could render a full viewer; for completeness we open a simple print-ready view
      const viewer = window.open('', '_blank')
      if (viewer) {
        viewer.document.write(`<pre style="font-family:Inter,system-ui;padding:40px;max-width:720px;margin:40px auto;line-height:1.65;font-size:14.5px">${JSON.stringify({ capsule: c, orientation: c.orientation_text }, null, 2)}</pre>`)
      }
    } catch {
      toast(`Capsule: ${c.title}`, { description: 'Local artifact — would fetch full immutable bundle from R2 in production.' })
    }
  }

  // Full user rights — delete everything
  async function purgeAll() {
    if (!confirm('Permanently delete all your contributions, attestations, and nuances across co-ops? This cannot be undone.')) return
    const o = await ensureHashed(owner)
    try {
      await api('/api/purge', { method: 'POST', body: JSON.stringify({ secret: o.secret }) })
    } catch {}
    localStorage.removeItem('cc_owner')
    setOwner(getOrCreateOwner())
    setDebts([])
    setNuances({})
    setCapsules([])
    window.location.reload()
  }

  const currentThreshold = activeCoop?.evidence_threshold || 3.5
  const visibleCount = mapPoints.length

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* Cooperative, sturdy nav */}
      <nav className="sticky top-0 z-50 border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5 font-semibold tracking-[-0.4px] text-xl">
              <div className="w-6 h-6 rounded bg-[#4a5f48] flex items-center justify-center text-[#f8f5f0]"><Map size={15} /></div>
              <span>courage-coop</span>
            </div>
            <div className="hidden md:flex items-center gap-1 text-sm text-[var(--text-muted)] pl-2">
              <button onClick={() => setActiveView('map')} className={activeView === 'map' ? 'font-medium text-[var(--text)] px-2' : 'hover:text-[var(--text)] px-2'}>The Map</button>
              <button onClick={() => setActiveView('submit')} className={activeView === 'submit' ? 'font-medium text-[var(--text)] px-2' : 'hover:text-[var(--text)] px-2'}>Record a Small Ask</button>
              <button onClick={() => setActiveView('nuances')} className={activeView === 'nuances' ? 'font-medium text-[var(--text)] px-2' : 'hover:text-[var(--text)] px-2'}>Nuance Machine</button>
              <button onClick={() => setActiveView('capsules')} className={activeView === 'capsules' ? 'font-medium text-[var(--text)] px-2' : 'hover:text-[var(--text)] px-2'}>Memory Capsules</button>
              <button onClick={() => setActiveView('mine')} className={activeView === 'mine' ? 'font-medium text-[var(--text)] px-2' : 'hover:text-[var(--text)] px-2'}>My Contributions</button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CreatorLinks />
            <button onClick={() => { loadLiveMap(activeCoopId); toast('Synced') }} className="btn btn-ghost text-xs px-3 py-1"><RefreshCw size={14} /> Sync</button>
          </div>
        </div>

        {/* Co-op switcher — central to the cooperative experience */}
        <div className="border-t border-[var(--border)] bg-[var(--bg-elev)]">
          <div className="max-w-6xl mx-auto px-5 py-2.5 flex items-center gap-2 overflow-x-auto">
            <div className="text-[10px] uppercase tracking-[2px] text-[var(--text-subtle)] mr-1">Cooperative</div>
            {coops.map(c => (
              <button key={c.id} onClick={() => switchCoop(c.id)}
                className={`chip text-xs whitespace-nowrap ${activeCoopId === c.id ? 'selected' : ''}`}>
                {c.name}
              </button>
            ))}
            <div className="ml-auto text-[10px] text-[var(--text-subtle)] hidden sm:block">
              Evidence threshold: <span className="tabular-nums font-medium text-[var(--text)]">{currentThreshold}</span> • {visibleCount} visible
            </div>
          </div>
        </div>
      </nav>

      {/* Orientation */}
      <div className="max-w-4xl mx-auto px-5 pt-10 pb-7 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--bg-panel)] px-4 py-1 text-[10px] tracking-[2px] text-[var(--text-subtle)] mb-4">
          EVIDENCE WEIGHTING • PUBLIC NUANCE MACHINE • COMMUNITY MEMORY CAPSULES
        </div>
        <h1 className="text-5xl md:text-[54px] leading-[0.9] tracking-[-2.4px] font-semibold">The courage to name a small need<br />keeps the cooperative alive.</h1>
        <p className="mt-4 text-[15.5px] text-[var(--text-muted)] max-w-2xl mx-auto">
          A live cognitive map for cooperatives. Only contributions with sufficient community evidence appear. 
          Nuances add texture without flattening. Capsules are the portable, archivable unit of group memory.
        </p>
        <div className="mt-4"><CreatorLinks variant="hero" /></div>
        <div className="mt-3 text-[10px] text-[var(--text-subtle)]">No accounts. No therapy framing. You decide what is mapped and what is remembered.</div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pb-20">
        {/* THE MAP */}
        {activeView === 'map' && (
          <div className="space-y-6">
            <div className="flex items-end justify-between">
              <div>
                <div className="section-title">LIVE COGNITIVE MAP</div>
                <div className="text-3xl tracking-tight font-semibold mt-1">What the cooperative can see depends on evidence.</div>
              </div>
              <button onClick={() => setActiveView('submit')} className="btn btn-primary"><Plus size={16} /> Record a small ask</button>
            </div>

            <div className="card p-5">
              <CognitiveMap 
                points={mapPoints} 
                personalDebtIds={personalDebtIds} 
                coop={activeCoop}
                onPointClick={(p) => { const debt = debts.find(d => d.id === p.id); if (debt) openDebt(debt); }}
                onRunNuanceMachine={() => runNuanceMachine()}
              />
            </div>

            <div className="grid md:grid-cols-5 gap-4 text-sm">
              <div className="md:col-span-3 card p-5">
                <div className="section-title mb-2">MAP GRAMMAR — EVIDENCE-WEIGHTED</div>
                <ul className="space-y-1.5 text-[var(--text-muted)] text-[14.2px]">
                  <li><strong>X axis</strong> — Avoidance intensity (how much courage / rehearsal / delay the ask required). Right = rarely surfaced.</li>
                  <li><strong>Y axis</strong> — Repair orientation (willingness + presence of a concrete closure action that can be repeated).</li>
                  <li><strong>Point size + rings</strong> — Evidence weight. Starts from self-report + evidence types chosen. Grows with attestations, successful concrete closures, and repetition across members.</li>
                  <li><strong>Contours</strong> — Density of high-evidence, high-repair patterns. These are the cooperative’s visible relational infrastructure.</li>
                </ul>
                <div className="mt-3 text-xs text-[var(--text-subtle)]">Only debts with explicit map consent and evidence weight above the co-op’s threshold are shown. Nothing is mapped without consent.</div>
              </div>
              <div className="md:col-span-2 card p-5 text-sm space-y-3">
                <div className="section-title">CURRENT CO-OP FILTER</div>
                <div className="text-[var(--text-muted)]">{activeCoop?.description}</div>
                <div>Evidence threshold: <span className="font-medium tabular-nums">{currentThreshold}</span></div>
                <div className="text-xs text-[var(--text-subtle)] pt-1">Change co-op in the bar above to see different maps and different thresholds. A contribution can live in multiple co-ops if the member belongs to them.</div>
                <button onClick={() => setActiveView('submit')} className="btn btn-secondary w-full mt-2">Record something for this co-op</button>
              </div>
            </div>
          </div>
        )}

        {/* SUBMIT — the four fields + evidence weighting + granular consent */}
        {activeView === 'submit' && (
          <div className="max-w-3xl">
            <div className="mb-5">
              <div className="section-title">RECORD A SMALL ASK</div>
              <div className="text-3xl tracking-tight font-semibold mt-1">Capture the courage that usually stays invisible.</div>
            </div>

            <div className="card p-6 space-y-6">
              <div>
                <div className="section-title mb-1.5">THE SOCIAL DEBT ITEM</div>
                <textarea 
                  className="input w-full h-20 p-3 text-[15px]" 
                  placeholder="What small coordination or capacity ask did someone (or you) finally surface that had been avoided?"
                  value={form.social_debt_item} 
                  onChange={e => setForm(f => ({ ...f, social_debt_item: e.target.value }))} 
                />
                <div className="text-[10px] text-[var(--text-subtle)] mt-1">Keep it concrete and structural. “Asking the circle to name a rotating note-taker” is stronger than “I felt bad asking for help”.</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <div className="section-title mb-1">AVOIDANCE INTENSITY (0–10)</div>
                  <input type="range" min={0} max={10} step={0.1} value={form.avoidance_intensity} onChange={e => setForm(f => ({ ...f, avoidance_intensity: parseFloat(e.target.value) }))} className="slider w-full" />
                  <div className="text-right tabular-nums text-sm font-medium">{form.avoidance_intensity.toFixed(1)}</div>
                  <div className="text-xs text-[var(--text-subtle)]">How much internal rehearsal, delay, or courage was required to name it?</div>
                </div>
                <div>
                  <div className="section-title mb-1">REPAIR WILLINGNESS (1–7)</div>
                  <input type="range" min={1} max={7} step={1} value={form.repair_willingness} onChange={e => setForm(f => ({ ...f, repair_willingness: parseInt(e.target.value) }))} className="slider w-full" />
                  <div className="text-right tabular-nums text-sm font-medium">{form.repair_willingness}</div>
                  <div className="text-xs text-[var(--text-subtle)]">How willing were the people involved to make it right if it went poorly?</div>
                </div>
              </div>

              <div>
                <div className="section-title mb-1.5">CLOSURE ACTION — WHAT ACTUALLY RESOLVED IT (OR WOULD)</div>
                <textarea className="input w-full h-16 p-3" placeholder="The concrete move that closed the loop or turned the one-time ask into repeatable infrastructure."
                  value={form.closure_action} onChange={e => setForm(f => ({ ...f, closure_action: e.target.value }))} />
                <div className="text-xs text-[var(--text-subtle)] mt-1">Examples: “Added a standing 90-second access round”, “Wrote a one-page micro-loan template”, “Pinned a single borrow calendar”.</div>
              </div>

              {/* Evidence weighting controls — this is what decides what gets mapped */}
              <div>
                <div className="section-title mb-1.5">EVIDENCE — WHAT MAKES THIS VISIBLE TO THE CO-OP?</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {EVIDENCE_TYPES.map(et => (
                    <button key={et} onClick={() => setForm(f => ({ 
                      ...f, 
                      evidence_types: f.evidence_types.includes(et) 
                        ? f.evidence_types.filter(x => x !== et) 
                        : [...f.evidence_types, et] 
                    }))} className={`chip text-xs ${form.evidence_types.includes(et) ? 'selected' : ''}`}>
                      {et.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
                <input className="input w-full p-2.5 text-sm" placeholder="Optional short corroborating note (peer mentioned it, we have precedent in the bike co-op, etc.)"
                  value={form.evidence_notes} onChange={e => setForm(f => ({ ...f, evidence_notes: e.target.value }))} />
                <div className="text-[10px] text-[var(--text-subtle)] mt-1">This directly feeds the evidence weight calculation. More types + concrete closure + community attestations = higher chance of appearing on the map.</div>
              </div>

              {/* Granular consent — never assumed */}
              <div className="pt-2 border-t border-[var(--border)]">
                <div className="section-title mb-2">CONSENT — YOU DECIDE WHAT LEAVES YOUR DEVICE</div>
                <label className="flex items-start gap-3 text-sm mb-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.map_consent} onChange={e => setForm(f => ({ ...f, map_consent: e.target.checked ? 1 : 0 }))} className="mt-1" />
                  <span>Include in this cooperative’s live cognitive map (anonymized, evidence-weighted). Only the structured fields travel.</span>
                </label>
                <label className="flex items-start gap-3 text-sm mb-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.capsule_consent} onChange={e => setForm(f => ({ ...f, capsule_consent: e.target.checked ? 1 : 0 }))} className="mt-1" />
                  <span>Allow inclusion in future Community Memory Capsules sealed by stewards or members.</span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.nuance_consent} onChange={e => setForm(f => ({ ...f, nuance_consent: e.target.checked ? 1 : 0 }))} className="mt-1" />
                  <span>Open this record to public nuance contributions (the nuance machine and other members can add structural context).</span>
                </label>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <div className="text-xs text-[var(--text-muted)] tabular-nums">Computed evidence weight ≈ {computeEvidenceWeight(3 + form.evidence_types.length * 0.7, 0, form.closure_action.length > 12, form.evidence_types.includes('repeated_in_group')).toFixed(1)}</div>
                <button onClick={submitDebt} disabled={loading} className="btn btn-primary ml-auto px-8">SAVE TO THE COOPERATIVE</button>
              </div>
            </div>
          </div>
        )}

        {/* NUANCE MACHINE */}
        {activeView === 'nuances' && (
          <div className="max-w-3xl">
            <div className="mb-5">
              <div className="section-title">PUBLIC NUANCE MACHINE</div>
              <div className="text-3xl tracking-tight font-semibold mt-1">Texture without flattening.</div>
            </div>
            <div className="card p-6">
              <p className="text-[15px] text-[var(--text-muted)]">The nuance machine reads existing human nuances across the cooperative (via Vectorize) and, with careful prompting, surfaces structural patterns: what made repair possible, what turned a one-off ask into carried infrastructure, what raised or lowered avoidance for others.</p>
              <button onClick={() => runNuanceMachine()} className="btn btn-primary mt-5">Run nuance machine across current cooperative field</button>
              {machineSummary && (
                <div className="mt-5 p-4 bg-[var(--bg-elev)] border border-[var(--border)] rounded-xl text-[14.5px] leading-snug">
                  {machineSummary}
                </div>
              )}
              <div className="text-[10px] text-[var(--text-subtle)] mt-4">All summaries are offered as one contribution among many. Human nuances always take precedence. The machine is instructed never to psychologize or use shame/fear language.</div>
            </div>
          </div>
        )}

        {/* CAPSULES — the export/share/archive unit */}
        {activeView === 'capsules' && (
          <div>
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="section-title">COMMUNITY MEMORY CAPSULES</div>
                <div className="text-3xl tracking-tight font-semibold mt-1">The portable, archivable unit of cooperative memory.</div>
              </div>
              <button onClick={sealCapsule} className="btn btn-primary"><Archive size={16} /> Seal current state as capsule</button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {capsules.filter(c => c.coop_id === activeCoopId || c.coop_id === 'coop_solidarity').length === 0 && (
                <div className="card p-6 text-[var(--text-muted)]">No capsules sealed for this cooperative yet. Use the button above to create the first one.</div>
              )}
              {capsules.filter(c => c.coop_id === activeCoopId || !c.coop_id).map(c => (
                <div key={c.id} className="card p-5 capsule-card flex flex-col">
                  <div className="font-medium text-lg tracking-tight">{c.title}</div>
                  <div className="text-xs text-[var(--text-subtle)] mt-0.5">{new Date(c.sealed_at).toLocaleDateString()} • v{c.version} • {c.included_debt_count} debts • {c.included_nuance_count} nuances</div>
                  {c.orientation_text && <div className="mt-3 text-[14px] text-[var(--text-muted)] leading-snug border-l-2 border-[var(--accent)] pl-3">{c.orientation_text}</div>}
                  <div className="mt-auto pt-4 flex gap-2">
                    <button onClick={() => openCapsule(c)} className="btn btn-secondary text-xs"><Download size={14} /> View bundle</button>
                    <div className="text-[10px] text-[var(--text-subtle)] flex items-center gap-1 ml-auto"><Lock size={12} /> Immutable R2 artifact</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs text-[var(--text-subtle)] mt-6 max-w-prose">Capsules are the designed share unit. A cooperative can pass a capsule to new members, archive it for governance retros, or publish a redacted version. The full JSON + orientation text lives in R2 and can be deleted only by the sealing co-op’s authorized actors (in this starter, the person who sealed it).</div>
          </div>
        )}

        {/* MY CONTRIBUTIONS + rights */}
        {activeView === 'mine' && (
          <div className="max-w-3xl">
            <div className="mb-5">
              <div className="section-title">MY CONTRIBUTIONS</div>
              <div className="text-3xl tracking-tight font-semibold mt-1">Full control. Full visibility into what you have seeded.</div>
            </div>

            <div className="space-y-2 mb-8">
              {debts.filter(d => d.owner_hash === owner.hash || d.owner_hash === 'demo').length === 0 && <div className="text-[var(--text-muted)]">No contributions yet in this browser. Record something in the Submit view.</div>}
              {debts.filter(d => d.owner_hash === owner.hash || d.owner_hash === 'demo').map(d => (
                <div key={d.id} className="card p-3.5 text-sm flex flex-col md:flex-row md:items-center gap-2">
                  <div className="flex-1">
                    <div className="font-medium">{d.social_debt_item.slice(0, 90)}{d.social_debt_item.length > 90 ? '…' : ''}</div>
                    <div className="text-xs text-[var(--text-subtle)]">avoid {d.avoidance_intensity} · repair {d.repair_willingness} · evidence {d.evidence_weight.toFixed(1)}</div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => openDebt(d)} className="btn btn-ghost px-2">View nuances</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--border)] pt-6">
              <button onClick={purgeAll} className="flex items-center gap-2 text-sm text-[var(--anomaly)] hover:underline"><Trash2 size={15} /> Permanently delete everything I have contributed across all cooperatives</button>
              <div className="text-[10px] text-[var(--text-subtle)] mt-1.5 max-w-prose">This uses the same R2 + D1 delete paths that power capsule deletion and consent revocation. All objects and rows associated with your owner hash are removed.</div>
            </div>
          </div>
        )}
      </div>

      {/* Debt detail drawer with integrated Public Nuance Machine */}
      {selectedDebt && (
        <div className="drawer p-6">
          <button onClick={() => { setSelectedDebt(null); setMachineSummary('') }} className="absolute right-4 top-4 text-[var(--text-subtle)]"><X size={18} /></button>

          <div className="text-xs uppercase tracking-widest text-[var(--text-subtle)]">SOCIAL DEBT • {activeCoop?.name}</div>
          <div className="text-2xl tracking-tight font-semibold mt-1 pr-8">{selectedDebt.social_debt_item}</div>

          <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
            <div className="card p-3"><div className="text-[10px] text-[var(--text-subtle)]">AVOIDANCE</div><div className="text-xl font-semibold tabular-nums">{selectedDebt.avoidance_intensity}</div></div>
            <div className="card p-3"><div className="text-[10px] text-[var(--text-subtle)]">REPAIR WILLINGNESS</div><div className="text-xl font-semibold tabular-nums">{selectedDebt.repair_willingness}</div></div>
            <div className="card p-3"><div className="text-[10px] text-[var(--text-subtle)]">EVIDENCE WEIGHT</div><div className="text-xl font-semibold tabular-nums">{selectedDebt.evidence_weight.toFixed(1)}</div></div>
          </div>

          <div className="mt-4">
            <div className="section-title mb-1">CLOSURE ACTION</div>
            <div className="text-[15px] leading-snug">{selectedDebt.closure_action}</div>
          </div>

          {/* Attest / grow evidence */}
          <div className="mt-5">
            <div className="section-title mb-1.5">ADD COMMUNITY EVIDENCE</div>
            <div className="flex gap-2">
              {[1,2,3].map(s => <button key={s} onClick={() => attest(selectedDebt, s as 1|2|3)} className="btn text-xs">Corroborate ×{s}</button>)}
            </div>
          </div>

          {/* Nuances + add new + machine */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <div className="section-title">PUBLIC NUANCES ({drawerNuances.length})</div>
              <button onClick={() => runNuanceMachine(selectedDebt)} className="text-xs btn btn-ghost">Run nuance machine here</button>
            </div>

            <div className="space-y-3 mt-2">
              {drawerNuances.length === 0 && <div className="text-sm text-[var(--text-muted)]">No nuances yet. Add the first structural observation.</div>}
              {drawerNuances.map(n => (
                <div key={n.id} className="nuance p-3 rounded-xl text-[14px]">
                  <div className="text-[10px] text-[var(--text-subtle)] mb-0.5">{n.source === 'machine' ? 'MACHINE SYNTHESIS' : 'HUMAN CONTRIBUTION'}</div>
                  {n.text}
                </div>
              ))}
            </div>

            {selectedDebt.nuance_consent === 1 && (
              <div className="mt-4">
                <textarea className="input w-full h-16 p-3 text-sm" placeholder="Add a structural nuance (what made repair possible? what pattern does this reveal? what turned the one-time ask into carried infrastructure?)" value={newNuanceText} onChange={e => setNewNuanceText(e.target.value)} />
                <button onClick={addNuance} disabled={!newNuanceText.trim()} className="btn btn-primary text-xs mt-2">Add nuance to the public record</button>
              </div>
            )}
          </div>

          {machineSummary && (
            <div className="mt-5 p-3 bg-[var(--bg-panel)] rounded-xl text-sm border border-[var(--border)]">{machineSummary}</div>
          )}

          <div className="text-[10px] text-[var(--text-subtle)] mt-6">This record can be removed from future capsules and the map by its originator. Nuances contributed by others remain part of the cooperative memory unless the entire capsule is superseded.</div>
        </div>
      )}

      <footer className="border-t border-[var(--border)] py-8 mt-12 text-xs">
        <div className="max-w-5xl mx-auto px-5 flex flex-col md:flex-row gap-y-3 md:items-center justify-between text-[var(--text-subtle)]">
          <div>Built for cooperatives that want to see and carry the relational labor that usually stays invisible.</div>
          <CreatorLinks variant="footer" />
        </div>
        <div className="max-w-5xl mx-auto px-5 mt-4 text-[10px] text-[var(--text-subtle)]/70">
          Evidence weighting decides visibility. The public nuance machine adds texture. Community memory capsules are the share and archive unit. Nothing here extracts vulnerability for its own sake.
        </div>
      </footer>
    </div>
  )
}
